import React, {useCallback, useEffect, useState} from 'react'
import { Modal } from 'react-bootstrap'
import { Button, PrimaryButton } from '../styled-components/buttons'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { newContact } from '../requests'
import Warning from "./Warning";
import {error} from "../colors";

const ConsentButton = styled(Button)`
  background-color: #aaa;
  margin-right: 20px;
  &:hover {
    background-color: #666;
  }
`

const ConsentWarning = styled(Warning)`
  color: ${error};
`

interface IConsentModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  onConsent: () => void
  organizationId: number
  userId: number
}

export const ConsentModal: React.FC<IConsentModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  onConsent,
  organizationId,
  userId,
}) => {
  const userSharedInformationTranslation = [
    'consent.picture',
    'consent.full_name',
    'consent.date_of_birth',
    'consent.gender',
    'consent.sin',
    'consent.phone_numbers',
    'consent.addresses',
    'consent.email',
  ];

  const memberSharedInformationTranslation = [
    'consent.picture',
    'consent.full_name',
    'consent.date_of_birth',
    'consent.gender',
  ];

  const getErrorTranslation = (errorCode: string):string => {
    switch (errorCode) {
      case "CONTACT_ALREADY_EXISTS":
        return 'consent.errors.CONTACT_ALREADY_EXISTS';
      case "ORGANIZATION_DOES_NOT_EXIST":
        return 'consent.errors.ORGANIZATION_DOES_NOT_EXIST';
      default:
        return 'consent.errors.UNKNOWN';
    }
  }

  useEffect(() => {
    if (isOpen) {
      const modal = document.getElementById('consent-modal');
      if (modal) {
        window.scrollTo({
          top: modal.offsetHeight,
          behavior: 'smooth'
        });
      }
    }
  }, [isOpen]);

  const { t } = useTranslation();
  const [errorCode, setErrorCode] = useState<string|null>(null);
  const handleConsent = useCallback(
  () => {
    newContact(organizationId, userId)
      .then(() => {
        onConfirm();
        onConsent ? onConsent() : onClose();
      })
      .catch((error) => {
        const errorCode = error.message && error.status === 422
          ? error.message
          : "UNKNOWN"
        ;

        setErrorCode(errorCode);
      })
    },
    [organizationId, userId, onConsent, onClose, setErrorCode],
  )

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      id={"consent-modal"}
      show={isOpen}
      onHide={onClose}
      backdrop="static"
      dialogClassName="modal-dialog_activity"
      contentClassName={"consent-modal"}
      animation={false}
      centered
    >
      <div className={'subscription-confirmation-modal-body'}>
        <Modal.Header closeButton={true}>
          <Modal.Title className={"title"}>
            <h1>{t('consent.title')}</h1>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className={"description"}>{t('consent.description')}</p>
          <p className={"sub-description"}>{t('consent.subdescription')}</p>
          <ul className={"list"}>
            {userSharedInformationTranslation.map((key) => (
              <li key={key}>
                {t(key)}
              </li>
            ))}
            <li>
              {t('consent.family_members')}
              <ul>
                {memberSharedInformationTranslation.map((key) => (
                  <li key={key}>
                    {t(key)}
                  </li>
                ))}
              </ul>
            </li>
          </ul>
        </Modal.Body>
      </div>
      <Modal.Footer>
        <PrimaryButton className={"btn btn-cancel"} onClick={onClose}>
          {t('consent.decline')}
        </PrimaryButton>
        <ConsentButton className={"btn btn-confirm"} onClick={handleConsent}>
          {t('consent.consent')}
        </ConsentButton>
      </Modal.Footer>
      {
        errorCode && <ConsentWarning
          messages = { [t(getErrorTranslation(errorCode))] }
        />
      }
    </Modal>
  )
}
